// billingApi — typed fetchers for /v1/billing/* (Route 13).
import { apiFetch, resolveApiBase } from "../../lib/api-client.js";

export interface InvoiceRow {
  id: string;
  tenant_id: string;
  period: string;
  amount_cents: number;
  currency: string;
  locations: number;
  status: "draft" | "due" | "paid" | "overdue" | "void" | string;
  issued_at: number | null;
  due_at: number | null;
  paid_at: number | null;
  pdf_r2_key: string | null;
  created_at: number;
}

export interface UsageRow {
  tenant_id: string;
  period: string;
  locations: number;
  events_total: number;
  events_verified: number;
  computed_at: number;
}

export class BillingApiError extends Error {
  status: number; code: string; correlationId: string | null;
  constructor(status: number, code: string, correlationId: string | null) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId;
  }
}
export function friendlyBillingError(err: unknown): string {
  if (err instanceof BillingApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色看不到計費資料。";
    if (err.status === 404 && err.code === "pdf_not_ready") return "本期帳單 PDF 尚未產生，請等待月結算 cron 執行。";
    if (err.status === 404) return "找不到此項目。";
    if (err.status === 400 && err.code === "tenant_id_required") return "尚未選定操作的單位。";
    return "計費資料暫時無法存取。";
  }
  return "連線中斷，請檢查網路後再試。";
}
async function asError(r: Response): Promise<BillingApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id");
  try { const b = (await r.json()) as { error?: string; correlation_id?: string }; code = b.error ?? code; cid = b.correlation_id ?? cid; } catch {}
  return new BillingApiError(r.status, code, cid);
}

export async function listInvoices(tenantId?: string | null): Promise<{ items: InvoiceRow[]; correlation_id: string | null }> {
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  const r = await apiFetch(`/v1/billing/invoices${qs}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: InvoiceRow[]; correlation_id: string | null };
}
export async function listUsage(tenantId?: string | null, from?: string | null, to?: string | null): Promise<{ items: UsageRow[]; correlation_id: string | null }> {
  const qs = new URLSearchParams();
  if (tenantId) qs.set("tenant_id", tenantId);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const r = await apiFetch(`/v1/billing/usage${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { items: UsageRow[]; correlation_id: string | null };
}

export function invoicePdfUrl(invoiceId: string, tenantId?: string | null): string {
  const base = resolveApiBase();
  const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
  return `${base}/v1/billing/invoices/${encodeURIComponent(invoiceId)}/pdf${qs}`;
}
